import { backendClient } from '../../api/backend'
import { backendUrl } from '../../config'
import {
  BO_CONSOL_FETCH_LIST_REQUEST, BO_CONSOL_FETCH_LIST_SUCCESS, BO_CONSOL_FETCH_LIST_FAILURE,
  BO_CONSOL_SELECT_BO, BO_CONSOL_SET_PHASE,
  BO_CONSOL_CLEAN_START, BO_CONSOL_CLEAN_PROGRESS, BO_CONSOL_CLEAN_COMPLETE, BO_CONSOL_CLEAN_ERROR,
  BO_CONSOL_FETCH_EXTRACTED_REQUEST, BO_CONSOL_FETCH_EXTRACTED_SUCCESS, BO_CONSOL_FETCH_EXTRACTED_FAILURE,
  BO_CONSOL_UPDATE_DOC_STATUS,
  BO_CONSOL_EXECUTE_START, BO_CONSOL_EXECUTE_PROGRESS, BO_CONSOL_EXECUTE_COMPLETE, BO_CONSOL_EXECUTE_ERROR,
  BO_CONSOL_RESET, BO_CONSOL_SET_LIST_PAGE, BO_CONSOL_SET_LIST_FILTER, BO_CONSOL_SET_LIST_SEARCH,
  BO_CONSOL_SET_LIST_LANGUAGE, BO_CONSOL_SET_LIST_DATE_RANGE,
  BO_CONSOL_SYNC_START, BO_CONSOL_SYNC_COMPLETE, BO_CONSOL_SYNC_ERROR,
  BO_CONSOL_PREVIEW_BULK_START, BO_CONSOL_PREVIEW_BULK_PROGRESS, BO_CONSOL_PREVIEW_BULK_COMPLETE, BO_CONSOL_PREVIEW_BULK_ERROR,
  BO_CONSOL_PREVIEW_SINGLE_START, BO_CONSOL_PREVIEW_SINGLE_COMPLETE, BO_CONSOL_PREVIEW_SINGLE_ERROR,
  BO_CONSOL_FETCH_PREVIEW_REQUEST, BO_CONSOL_FETCH_PREVIEW_SUCCESS, BO_CONSOL_FETCH_PREVIEW_FAILURE,
  BO_CONSOL_CONFIRM_PREVIEW_SUCCESS,
  BO_CONSOL_OPEN_PREVIEW, BO_CONSOL_CLOSE_PREVIEW,
} from './types'

// ─── SSE Stream Helper ──────────────────────────────────────────────────────

const connectSSE = async (taskId, { onProgress, onComplete, onError }) => {
  const token = backendClient.getToken()
  const streamUrl = `${backendUrl}api/bo-consolidation/tasks/${taskId}/stream`

  const response = await fetch(streamUrl, {
    headers: token && token.access_token
      ? { Authorization: `Bearer ${token.access_token}` }
      : {},
  })

  const reader = response.body.getReader()
  const decoder = new TextDecoder("utf-8")
  let buffer = ''

  while (true) {
    const { done, value } = await reader.read()
    if (done) break

    buffer += decoder.decode(value, { stream: true })
    const lines = buffer.split('\n')
    buffer = lines.pop() // keep incomplete line in buffer

    for (const line of lines) {
      if (line.startsWith('data: ')) {
        try {
          const event = JSON.parse(line.slice(6))
          if (event.error) {
            onError(event.message || 'Processing failed')
          } else if (event.step === 'review' || event.step === 'complete') {
            onComplete(event)
          } else {
            onProgress(event)
          }
        } catch (e) {
          // ignore parse errors on SSE lines
        }
      }
    }
  }
}

// ─── Action Creators ────────────────────────────────────────────────────────

export const fetchBoList = (page = 1, pageSize = 20, status = 'all', search = '', language = 'all', dateFrom = '', dateTo = '') => async (dispatch) => {
  dispatch({ type: BO_CONSOL_FETCH_LIST_REQUEST })
  try {
    const data = await backendClient.getBoConsolidationList(page, pageSize, status, search, language, dateFrom, dateTo)
    dispatch({ type: BO_CONSOL_FETCH_LIST_SUCCESS, payload: data })
  } catch (error) {
    dispatch({ type: BO_CONSOL_FETCH_LIST_FAILURE, payload: error.message })
  }
}

export const selectBo = (bo) => ({
  type: BO_CONSOL_SELECT_BO,
  payload: bo,
})

const listenToCleaningSSE = (taskId) => async (dispatch) => {
  dispatch({ type: BO_CONSOL_CLEAN_START, payload: taskId })

  await connectSSE(taskId, {
    onProgress: (event) => {
      dispatch({
        type: BO_CONSOL_CLEAN_PROGRESS,
        payload: {
          step: event.step,
          percent: event.progress,
          message: event.message,
          docsFound: event.docs_found || 0,
        },
      })
    },
    onComplete: (event) => {
      dispatch({ type: BO_CONSOL_CLEAN_COMPLETE, payload: event.result || {} })
    },
    onError: (message) => {
      dispatch({ type: BO_CONSOL_CLEAN_ERROR, payload: message })
    },
  })
}

export const startCleaning = (boDbId) => async (dispatch) => {
  try {
    const data = await backendClient.startBoCleaning(boDbId)
    await dispatch(listenToCleaningSSE(data.task_id))
  } catch (error) {
    // 409 = already processing — reconnect to existing SSE stream
    if (error.status === 409 && error.detail?.task_id) {
      await dispatch(listenToCleaningSSE(error.detail.task_id))
      return
    }
    dispatch({ type: BO_CONSOL_CLEAN_ERROR, payload: error.message })
  }
}

export const fetchExtractedDocuments = (boDbId) => async (dispatch) => {
  dispatch({ type: BO_CONSOL_FETCH_EXTRACTED_REQUEST })
  try {
    const data = await backendClient.getBoExtractedDocuments(boDbId)
    dispatch({ type: BO_CONSOL_FETCH_EXTRACTED_SUCCESS, payload: data })
  } catch (error) {
    dispatch({ type: BO_CONSOL_FETCH_EXTRACTED_FAILURE, payload: error.message })
  }
}

export const updateDocValidation = (docId, validationStatus) => async (dispatch) => {
  try {
    const data = await backendClient.updateBoDocValidation(docId, validationStatus)
    dispatch({ type: BO_CONSOL_UPDATE_DOC_STATUS, payload: { docId, validationStatus, ...data } })
  } catch (error) {
    // silently fail or could dispatch error
    console.error('Failed to update doc validation:', error.message)
  }
}

export const approveAll = (boDbId, nextStep) => async (dispatch) => {
  try {
    await backendClient.approveAllBoDocs(boDbId, nextStep)
    dispatch(fetchExtractedDocuments(boDbId))
  } catch (error) {
    console.error('Failed to approve all docs:', error.message)
  }
}

export const startExecution = (boDbId) => async (dispatch, getState) => {
  // Gate: if there are still 'update' docs not yet 'confirmed', kick off bulk preview first
  const state = getState()
  const updates = (state.boConsolidation.extractedDocs?.update || [])
  const pendingPreview = updates.filter(
    (d) => ['approved', 'previewed', 'preview_failed'].includes(d.validation_status),
  )

  if (pendingPreview.length > 0) {
    // Need to generate / confirm previews first — switch to preview phase
    return dispatch(previewAllConsolidations(boDbId))
  }

  try {
    const data = await backendClient.startBoExecution(boDbId)
    const taskId = data.task_id

    dispatch({ type: BO_CONSOL_EXECUTE_START, payload: taskId })

    await connectSSE(taskId, {
      onProgress: (event) => {
        dispatch({
          type: BO_CONSOL_EXECUTE_PROGRESS,
          payload: {
            step: event.step,
            percent: event.progress,
            message: event.message,
            current: event.current || 0,
            total: event.total || 0,
          },
        })
      },
      onComplete: (event) => {
        dispatch({ type: BO_CONSOL_EXECUTE_COMPLETE, payload: event.result || {} })
      },
      onError: (message) => {
        dispatch({ type: BO_CONSOL_EXECUTE_ERROR, payload: message })
      },
    })
  } catch (error) {
    dispatch({ type: BO_CONSOL_EXECUTE_ERROR, payload: error.message })
  }
}

// ─── Consolidation Preview ──────────────────────────────────────────────────

export const previewAllConsolidations = (boDbId) => async (dispatch) => {
  dispatch({ type: BO_CONSOL_PREVIEW_BULK_START })
  try {
    const data = await backendClient.startBoPreviewAll(boDbId)
    const taskId = data.task_id

    await connectSSE(taskId, {
      onProgress: (event) => {
        dispatch({
          type: BO_CONSOL_PREVIEW_BULK_PROGRESS,
          payload: {
            step: event.step,
            percent: event.progress,
            message: event.message,
            current: event.current || 0,
            total: event.total || 0,
          },
        })
      },
      onComplete: (event) => {
        dispatch({ type: BO_CONSOL_PREVIEW_BULK_COMPLETE, payload: event.result || {} })
        // Refresh extracted docs so the new validation_status is reflected
        dispatch(fetchExtractedDocuments(boDbId))
      },
      onError: (message) => {
        dispatch({ type: BO_CONSOL_PREVIEW_BULK_ERROR, payload: message })
      },
    })
  } catch (error) {
    dispatch({ type: BO_CONSOL_PREVIEW_BULK_ERROR, payload: error.message })
  }
}

export const previewSingleConsolidation = (docId) => async (dispatch, getState) => {
  dispatch({ type: BO_CONSOL_PREVIEW_SINGLE_START, payload: { docId } })
  try {
    const data = await backendClient.startBoSinglePreview(docId)
    const taskId = data.task_id

    await connectSSE(taskId, {
      onProgress: () => { /* single previews don't render granular progress in UI */ },
      onComplete: () => {
        dispatch({ type: BO_CONSOL_PREVIEW_SINGLE_COMPLETE, payload: { docId } })
        // Re-fetch the cached preview content
        dispatch(fetchConsolidationPreview(docId))
      },
      onError: (message) => {
        dispatch({ type: BO_CONSOL_PREVIEW_SINGLE_ERROR, payload: { docId, error: message } })
      },
    })
  } catch (error) {
    dispatch({ type: BO_CONSOL_PREVIEW_SINGLE_ERROR, payload: { docId, error: error.message } })
  }
}

export const fetchConsolidationPreview = (docId) => async (dispatch) => {
  dispatch({ type: BO_CONSOL_FETCH_PREVIEW_REQUEST })
  try {
    const data = await backendClient.getBoConsolidationPreview(docId)
    dispatch({ type: BO_CONSOL_FETCH_PREVIEW_SUCCESS, payload: data })
  } catch (error) {
    dispatch({ type: BO_CONSOL_FETCH_PREVIEW_FAILURE, payload: error.message })
  }
}

export const confirmConsolidationPreview = (docId, { merged_html, metadata_json } = {}) => async (dispatch) => {
  try {
    await backendClient.confirmBoConsolidationPreview(docId, { merged_html, metadata_json })
    dispatch({
      type: BO_CONSOL_CONFIRM_PREVIEW_SUCCESS,
      payload: { docId, merged_html, metadata_json },
    })
  } catch (error) {
    console.error('Failed to confirm consolidation preview:', error.message)
    throw error
  }
}

export const openConsolidationPreview = (docId) => ({
  type: BO_CONSOL_OPEN_PREVIEW,
  payload: docId,
})

export const closeConsolidationPreview = () => ({ type: BO_CONSOL_CLOSE_PREVIEW })

export const syncFromSgg = () => async (dispatch) => {
  dispatch({ type: BO_CONSOL_SYNC_START })
  try {
    const data = await backendClient.syncBoFromSgg()
    dispatch({ type: BO_CONSOL_SYNC_COMPLETE, payload: data })
    dispatch(fetchBoList())
  } catch (error) {
    dispatch({ type: BO_CONSOL_SYNC_ERROR, payload: error.message })
  }
}

export const resetConsolidation = () => ({ type: BO_CONSOL_RESET })

export const setListPage = (page) => ({ type: BO_CONSOL_SET_LIST_PAGE, payload: page })
export const setListFilter = (status) => ({ type: BO_CONSOL_SET_LIST_FILTER, payload: status })
export const setListSearch = (search) => ({ type: BO_CONSOL_SET_LIST_SEARCH, payload: search })
export const setListLanguage = (language) => ({ type: BO_CONSOL_SET_LIST_LANGUAGE, payload: language })
export const setListDateRange = (dateFrom, dateTo) => ({ type: BO_CONSOL_SET_LIST_DATE_RANGE, payload: { dateFrom, dateTo } })
